/**
 * useTenantNav — 인증 후 네비게이션 표준 진입점.
 *
 * BE 메뉴 path·내부 라우트 push 는 app path(`/erp/...`, `/`) 형태다.
 * 이 composable 은 현재 사용자(`auth.user.tenantCode`) prefix 를 자동 부여해
 * `/{tenantCode}/...` 경로로 변환·네비게이션한다.
 *
 * 설계: docs/designs/tenant-url-prefix-persistence.md §5.5
 */
import { computed, type ComputedRef } from 'vue';
import { useRouter, type NavigationFailure } from 'vue-router';

import { tenantPath } from '../router/login-route';
import { useAuthStore } from '../stores/auth';

export interface TenantNavApi {
  /** 현재 사용자 테넌트 코드 (`''` 이면 prefix 미부여). */
  tenant: ComputedRef<string>;
  /** app path → 현재 테넌트 prefix 부여한 절대 경로 문자열. */
  to: (appPath: string) => string;
  push: (appPath: string) => Promise<void | NavigationFailure | undefined>;
  replace: (appPath: string) => Promise<void | NavigationFailure | undefined>;
}

export function useTenantNav(): TenantNavApi {
  const auth = useAuthStore();
  const router = useRouter();

  const tenant = computed<string>(() => auth.user?.tenantCode ?? '');
  const to = (appPath: string): string => {
    // POP(`/pop/*`)·인쇄(`/reports/*`) 등 의도적으로 prefix 없이 top-level 로
    // 등록된 라우트는 그대로 반환한다. prefix 를 붙이면 not-found 로 빠져
    // 메뉴 링크가 404 가 된다 (라우터 가드 (3) not-found 분기 주석 참고).
    if (appPath.startsWith('/')) {
      const direct = router.resolve(appPath);
      // `/:tenantCode` 매칭(단일 세그먼트를 테넌트로 오인)은 제외 — 진짜
      // top-level 라우트로 매칭될 때만 prefix 를 생략한다.
      if (
        direct.name !== undefined &&
        direct.name !== 'not-found' &&
        direct.params.tenantCode === undefined
      ) {
        return appPath;
      }
    }
    return tenantPath(appPath, tenant.value);
  };
  const push = (appPath: string): Promise<void | NavigationFailure | undefined> =>
    router.push(to(appPath));
  const replace = (appPath: string): Promise<void | NavigationFailure | undefined> =>
    router.replace(to(appPath));

  return { tenant, to, push, replace };
}
